import { useEffect, useState, type KeyboardEvent } from "react";
import { X } from "lucide-react";
import {
  Button,
  DialogRoot,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  Input,
  MetaPill,
  toast,
} from "@flint/ui";
import { trpc } from "../../../lib/trpc.ts";
import { storeErrorMessage } from "../error-message.ts";

export interface ScaleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved: (scaleId: string) => void;
}

/**
 * S-PK3 · New size scale dialog (spec 13 R1, design 14).
 *
 * Name + a tag-input labels builder: type a label, Enter/comma commits it as a
 * removable chip; chips keep entry order (order is the contract). Built locally
 * from Input + MetaPill — there is no `@flint/ui` tag-input primitive yet.
 */
export function ScaleDialog({ open, onOpenChange, onSaved }: ScaleDialogProps) {
  const [name, setName] = useState("");
  const [labels, setLabels] = useState<string[]>([]);
  const [draft, setDraft] = useState("");

  useEffect(() => {
    if (open) {
      setName("");
      setLabels([]);
      setDraft("");
    }
  }, [open]);

  const create = trpc.storePacks.createScale.useMutation();

  function commitDraft() {
    const label = draft.trim();
    if (!label) return;
    if (label.length > 8) {
      toast.error("A size is up to 8 characters");
      return;
    }
    if (labels.includes(label)) {
      toast.error(`"${label}" is already in this scale`);
      return;
    }
    if (labels.length >= 24) {
      toast.error("A scale holds up to 24 sizes");
      return;
    }
    setLabels((l) => [...l, label]);
    setDraft("");
  }

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      commitDraft();
    }
  }

  async function submit() {
    if (!name.trim()) {
      toast.error("Name your scale");
      return;
    }
    if (labels.length === 0) {
      toast.error("Add at least one size");
      return;
    }
    try {
      const res = await create.mutateAsync({ name: name.trim(), labels });
      toast.success("Size scale created");
      onSaved(res.scaleId);
      onOpenChange(false);
    } catch (e) {
      toast.error("Couldn't create the scale", { description: storeErrorMessage(e) });
    }
  }

  return (
    <DialogRoot open={open} onOpenChange={onOpenChange}>
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle>New size scale</DialogTitle>
          <DialogDescription>
            In the order buyers read them — S, M, L… or 28, 30, 32…
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <label className="flex flex-col gap-1.5">
            <span className="text-sm font-medium text-ink">Scale name</span>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Alpha · Waist · Age"
              maxLength={32}
              autoFocus
            />
          </label>

          <div className="flex flex-col gap-1.5">
            <span className="text-sm font-medium text-ink">Sizes</span>
            {labels.length > 0 ? (
              <div className="flex flex-wrap gap-1.5">
                {labels.map((label, i) => (
                  <span key={label} className="inline-flex items-center gap-1">
                    <MetaPill>{label}</MetaPill>
                    <button
                      type="button"
                      aria-label={`Remove ${label}`}
                      onClick={() => setLabels((l) => l.filter((_, n) => n !== i))}
                      className="text-muted hover:text-ink"
                    >
                      <X size={12} aria-hidden />
                    </button>
                  </span>
                ))}
              </div>
            ) : null}
            <div className="flex gap-2">
              <Input
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={onKeyDown}
                onBlur={commitDraft}
                placeholder="Type a size, press Enter"
                maxLength={8}
                aria-label="Sizes"
              />
              <Button intent="secondary" size="sm" onClick={commitDraft} disabled={!draft.trim()}>
                Add
              </Button>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button intent="ghost" onClick={() => onOpenChange(false)} disabled={create.isPending}>
            Cancel
          </Button>
          <Button intent="primary" onClick={submit} disabled={create.isPending}>
            Create scale
          </Button>
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  );
}
